import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { media } from '../theme/tokens';

// --- Icon components (inline SVG, matching prototype) ---

const Icons = {
  home: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  distribution: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  pilotage: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 3v18h18" />
      <path d="m7 16 4-4 4 4 5-5" />
    </svg>
  ),
  communication: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 11l18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  ),
  campaigns: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  ),
  chevron: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
};

// --- Types ---

export interface NavSubItem {
  id: string;
  label: string;
  path: string;
  count?: number;
}

export interface NavGroup {
  id: string;
  label: string;
  icon: keyof typeof Icons;
  path?: string;
  items: NavSubItem[];
}

export interface NavSingleItem {
  id: string;
  label: string;
  icon: keyof typeof Icons;
  path: string;
}

export type NavEntry = NavSingleItem | NavGroup;

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry;
}

interface V2LayoutProps {
  children: React.ReactNode;
  navigation: NavEntry[];
  activePath?: string;
  userName?: string;
  userInitials?: string;
  onNavigate?: (path: string) => void;
}

export const V2Layout: React.FC<V2LayoutProps> = ({
  children,
  navigation,
  activePath,
  userName,
  userInitials = '?',
  onNavigate
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    for (const entry of navigation) {
      if (
        isGroup(entry) &&
        entry.items.some((i) => activePath?.startsWith(i.path))
      ) {
        initial.add(entry.id);
      }
    }
    return initial;
  });

  const toggleGroup = (id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isActive = (path: string) =>
    activePath === path || activePath?.startsWith(path + '/');

  return (
    <Shell>
      <Topbar>
        <MobileMenuBtn
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Toggle menu"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M3 12h18M3 6h18M3 18h18" />
          </svg>
        </MobileMenuBtn>
        <Brand>
          <BrandText>Wine Services</BrandText>
          <V2BadgeEl>v2</V2BadgeEl>
        </Brand>
        <TopbarRight>
          <Avatar>{userInitials}</Avatar>
        </TopbarRight>
      </Topbar>

      <LayoutGrid>
        <SidebarOverlay
          $open={sidebarOpen}
          onClick={() => setSidebarOpen(false)}
        />
        <Sidebar $open={sidebarOpen}>
          <SidebarNav>
            {navigation.map((entry) => {
              if (!isGroup(entry)) {
                return (
                  <SingleNavItem
                    key={entry.id}
                    $active={!!isActive(entry.path)}
                    onClick={() => {
                      onNavigate?.(entry.path);
                      setSidebarOpen(false);
                    }}
                  >
                    <NavIcon $active={!!isActive(entry.path)}>
                      {Icons[entry.icon]}
                    </NavIcon>
                    <span>{entry.label}</span>
                  </SingleNavItem>
                );
              }

              const expanded = expandedGroups.has(entry.id);
              const groupActive = entry.path
                ? !!isActive(entry.path)
                : entry.items.some((i) => !!isActive(i.path));

              return (
                <GroupWrap key={entry.id}>
                  <GroupHead>
                    <GroupLink
                      $active={entry.path ? !!isActive(entry.path) : false}
                      onClick={() => {
                        if (entry.path) {
                          onNavigate?.(entry.path);
                          setSidebarOpen(false);
                        }
                      }}
                    >
                      <NavIcon $active={groupActive}>
                        {Icons[entry.icon]}
                      </NavIcon>
                      <GroupLabel>{entry.label}</GroupLabel>
                    </GroupLink>
                    <GroupCount $expanded={expanded}>
                      {entry.items.length}
                    </GroupCount>
                    <GroupToggle
                      onClick={() => toggleGroup(entry.id)}
                      aria-label={`Toggle ${entry.label}`}
                    >
                      <ChevIcon $expanded={expanded}>{Icons.chevron}</ChevIcon>
                    </GroupToggle>
                  </GroupHead>
                  <GroupItems $expanded={expanded}>
                    {entry.items.map((item) => (
                      <SubNavItem
                        key={item.id}
                        $active={!!isActive(item.path)}
                        onClick={() => {
                          onNavigate?.(item.path);
                          setSidebarOpen(false);
                        }}
                      >
                        <SubDot $active={!!isActive(item.path)} />
                        <span>{item.label}</span>
                        {item.count !== undefined && (
                          <SubCount>{item.count}</SubCount>
                        )}
                      </SubNavItem>
                    ))}
                  </GroupItems>
                </GroupWrap>
              );
            })}
          </SidebarNav>

          <SidebarFoot>
            <FootUser>
              <Avatar>{userInitials}</Avatar>
              <FootUserName>{userName || 'User'}</FootUserName>
            </FootUser>
          </SidebarFoot>
        </Sidebar>

        <Main>{children}</Main>
      </LayoutGrid>
    </Shell>
  );
};

// --- Styled Components ---

const Shell = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Topbar = styled.header`
  height: 56px;
  background: var(--white);
  border-bottom: 1px solid var(--line);
  display: flex;
  align-items: center;
  padding: 0 16px;
  position: sticky;
  top: 0;
  z-index: 60;
  flex-shrink: 0;
  ${media.md} {
    padding: 0 28px;
  }
`;

const MobileMenuBtn = styled.button`
  background: none;
  border: none;
  color: var(--ink);
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  &:hover {
    background: var(--paper);
  }
  ${media.lg} {
    display: none;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 12px;
  ${media.lg} {
    margin-left: 0;
  }
`;

const BrandText = styled.span`
  font-family: var(--font-heading-semibold);
  font-weight: 600;
  font-size: 17px;
  letter-spacing: -0.02em;
`;

const V2BadgeEl = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--terracotta-soft);
  color: var(--terracotta-dark);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TopbarRight = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  background: var(--terracotta-soft);
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-weight: 600;
  color: var(--terracotta-dark);
  font-size: 13px;
  flex-shrink: 0;
`;

const LayoutGrid = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  position: relative;
`;

const SidebarOverlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(43, 61, 84, 0.4);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'all' : 'none')};
  transition: opacity 0.25s;
  z-index: 70;
  ${media.lg} {
    display: none;
  }
`;

const Sidebar = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 56px;
  left: 0;
  bottom: 0;
  width: 240px;
  background: var(--white);
  border-right: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  transform: translateX(${({ $open }) => ($open ? '0' : '-100%')});
  transition: transform 0.25s cubic-bezier(0.32, 0.72, 0, 1);
  z-index: 80;

  ${media.lg} {
    position: static;
    transform: none;
    width: 220px;
    flex-shrink: 0;
  }
`;

const SidebarNav = styled.nav`
  flex: 1;
  padding: 16px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Main = styled.main`
  flex: 1;
  padding: 20px 16px 60px;
  overflow-y: auto;
  min-width: 0;
  ${media.md} {
    padding: 28px 24px 60px;
  }
  ${media.lg} {
    padding: 28px 36px 60px;
  }
`;

// --- Nav icon ---

const NavIcon = styled.span<{ $active: boolean }>`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  svg {
    width: 100%;
    height: 100%;
    color: ${({ $active }) =>
      $active ? 'var(--terracotta)' : 'var(--ink-faint)'};
    transition: color 0.15s;
  }
`;

// --- Single item ---

const SingleNavItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  border-radius: 6px;
  border: none;
  background: ${({ $active }) => ($active ? 'var(--ink)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--paper)' : 'var(--ink-muted)')};
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  margin-bottom: 4px;

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--ink)' : 'var(--paper)')};
    color: ${({ $active }) => ($active ? 'var(--paper)' : 'var(--ink)')};
  }
`;

// --- Group ---

const GroupWrap = styled.div`
  margin-top: 2px;
  margin-bottom: 2px;
`;

const GroupHead = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  margin-bottom: 2px;
`;

const GroupLink = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ $active }) =>
    $active ? 'var(--terracotta-dark)' : 'var(--ink-muted)'};
  background: ${({ $active }) =>
    $active ? 'var(--terracotta-soft)' : 'transparent'};
  border: none;
  cursor: pointer;
  text-align: left;
  letter-spacing: -0.005em;
  min-width: 0;
  transition: all 0.15s;

  &:hover {
    background: ${({ $active }) =>
      $active ? 'var(--terracotta-soft)' : 'var(--paper)'};
    color: ${({ $active }) =>
      $active ? 'var(--terracotta-dark)' : 'var(--ink)'};
  }
  &:hover ${NavIcon} svg {
    color: var(--terracotta);
  }
`;

const GroupLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GroupCount = styled.span<{ $expanded: boolean }>`
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--ink-faint);
  font-weight: 500;
  padding: 1px 6px;
  transition: opacity 0.15s;
  opacity: ${({ $expanded }) => ($expanded ? 0 : 1)};
`;

const GroupToggle = styled.button`
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  border: none;
  background: transparent;
  border-radius: 5px;
  cursor: pointer;
  color: var(--ink-faint);
  flex-shrink: 0;
  transition: background 0.15s;
  &:hover {
    background: var(--paper);
    color: var(--ink-muted);
  }
`;

const ChevIcon = styled.span<{ $expanded: boolean }>`
  width: 12px;
  height: 12px;
  display: flex;
  transition: transform 0.2s;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'rotate(0)')};
  svg {
    width: 100%;
    height: 100%;
  }
`;

const GroupItems = styled.div<{ $expanded: boolean }>`
  overflow: hidden;
  max-height: ${({ $expanded }) => ($expanded ? '400px' : '0')};
  transition: max-height 0.25s ease-out;
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: ${({ $expanded }) => ($expanded ? '2px 4px 6px' : '0 4px')};
`;

const SubNavItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 12px 7px 22px;
  border-radius: 6px;
  border: none;
  background: ${({ $active }) => ($active ? 'var(--ink)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--paper)' : 'var(--ink-muted)')};
  text-align: left;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--ink)' : 'var(--paper)')};
    color: ${({ $active }) => ($active ? 'var(--paper)' : 'var(--ink)')};
  }
`;

const SubDot = styled.span<{ $active: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $active }) =>
    $active ? 'var(--terracotta)' : 'var(--line)'};
  flex-shrink: 0;
`;

const SubCount = styled.span`
  margin-left: auto;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--crimson);
  font-weight: 600;
`;

const SidebarFoot = styled.div`
  padding: 12px 14px;
  border-top: 1px solid var(--line);
  margin-top: auto;
`;

const FootUser = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FootUserName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
`;
